import { formatRating } from '../../utils/rating';
import { surveyorSupplementPath } from '../../utils/surveyorSupplementPath';
import useVisibleRefresh from '../../hooks/useVisibleRefresh';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import { useCallback, useContext, useEffect, useState } from "react";
import { LayoutContext } from "../../context";
import { APIGetSurveyorDashboard } from "../../api";
import Cookies from "js-cookie";

import CalendarMonthRoundedIcon   from '@mui/icons-material/CalendarMonthRounded';
import LocationOnRoundedIcon      from '@mui/icons-material/LocationOnRounded';
import AccessTimeRoundedIcon      from '@mui/icons-material/AccessTimeRounded';
import SquareFootRoundedIcon      from '@mui/icons-material/SquareFootRounded';
import PhoneRoundedIcon           from '@mui/icons-material/PhoneRounded';
import OpenInNewRoundedIcon       from '@mui/icons-material/OpenInNewRounded';
import WarningAmberRoundedIcon    from '@mui/icons-material/WarningAmberRounded';
import CheckCircleRoundedIcon     from '@mui/icons-material/CheckCircleRounded';
import HourglassEmptyRoundedIcon  from '@mui/icons-material/HourglassEmptyRounded';
import DrawRoundedIcon            from '@mui/icons-material/DrawRounded';
import PaidRoundedIcon            from '@mui/icons-material/PaidRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { useNavigate } from 'react-router';
import SurveyorEventCard from "./SurveyorEventCard";
import { useOpenEventChat } from "../../hooks/useOpenEventChat";

// ─── Мок-данные ───────────────────────────────────────────────────────────────
const MONTHS_SHORT = ["янв","фев","мар","апр","май","июн","июл","авг","сен","окт","ноя","дек"];

const STATE_CONFIG = {
  completed: { label: 'Завершено', color: '#10b981', bg: 'rgba(16,185,129,0.1)',  icon: <CheckCircleRoundedIcon sx={{ fontSize: 13 }} /> },
  in_progress: { label: 'В работе', color: '#0ea5e9', bg: 'rgba(14,165,233,0.1)',  icon: <HourglassEmptyRoundedIcon sx={{ fontSize: 13 }} /> },
  new:  { label: 'Новое', color: '#6366f1', bg: 'rgba(99,102,241,0.1)',  icon: null },
  measuring_premises: { label: 'Запланировано', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',  icon: <CalendarMonthRoundedIcon sx={{ fontSize: 13 }} /> },
  drawing: { label: 'На черчении', color: '#0ea5e9', bg: 'rgba(14,165,233,0.1)',  icon: <DrawRoundedIcon sx={{ fontSize: 13 }} /> },
};

function formatTime(t) { return t ? String(t).slice(0, 5) : null; }

function buildTimeString(item) {
  const from = formatTime(item?.time_from);
  const to   = formatTime(item?.time_to);
  const main = formatTime(item?.time_event);
  if (from && to) return `${from} — ${to}`;
  if (main) return main;
  return null;
}

function isOverdue(item) {
  if (item?.state === 'completed' || item?.is_completed) return false;
  if (!item?.date_event) return false;
  const parts = item.date_event.split('.');
  if (parts.length !== 3) return false;
  const d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
  return d < new Date(new Date().toDateString());
}


// ─── StatCard ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sublabel, color, icon, progress }) {
  return (
    <Card
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: { xs: "calc(50% - 6px)", sm: 150 },
        border: "1.5px solid",
        borderColor: "divider",
        borderRadius: "16px",
        bgcolor: "background.surface",
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        transition: "box-shadow 0.2s, transform 0.2s",
        "&:hover": { boxShadow: "0 6px 20px rgba(0,0,0,0.09)", transform: "translateY(-1px)" },
        p: 0,
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography sx={{ fontSize: "2.4rem", fontWeight: 800, lineHeight: 1, color: color || "text.primary", fontFamily: "'Sora', sans-serif", letterSpacing: "-1.5px" }}>
              {value}
            </Typography>
            <Typography level="body-xs" sx={{ mt: 0.5, color: "text.secondary", fontWeight: 600 }}>{label}</Typography>
            {sublabel && <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.2, fontSize: "0.68rem" }}>{sublabel}</Typography>}
          </Box>
          <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: color ? `${color}15` : "neutral.softBg", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.15rem" }}>
            {icon}
          </Box>
        </Stack>
        {progress !== undefined && (
          <LinearProgress value={progress} determinate sx={{ mt: 1.5, borderRadius: 8, height: 4, "--LinearProgress-progressColor": color || "#6366f1", "--LinearProgress-trackColor": "#f1f5f9" }} />
        )}
      </CardContent>
    </Card>
  );
}



// ─── FreeEventCard ────────────────────────────────────────────────────────────
function FreeEventCard({ item, onOpen, onTake }) {
  const deal    = item?.bitrix_deal;
  const city    = deal?.city?.name;
  const address = deal?.address;
  const full    = [city, address].filter(Boolean).join(', ') || '—';

  let day = '—', monthIdx = 0;
  if (item?.date_event) {
    const raw = item.date_event;
    if (raw.includes('.')) {
      const p = raw.split('.'); day = p[0]; monthIdx = Number(p[1]) - 1;
    } else {
      const p = raw.split('-'); day = p[2]; monthIdx = Number(p[1]) - 1;
    }
  }

  return (
    <Box sx={{
      borderRadius: '16px',
      border: '1.5px solid rgba(16,185,129,0.2)',
      background: 'white',
      boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
      overflow: 'hidden',
      transition: 'box-shadow 0.18s, transform 0.18s',
      cursor: 'pointer',
      '&:hover': { boxShadow: '0 6px 24px rgba(16,185,129,0.13)', transform: 'translateY(-1px)' },
    }}
      onClick={() => onOpen(item)}
    >
      <Box sx={{ height: 3, background: 'linear-gradient(90deg, #10b981, #34d399)' }} />
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          {/* Дата */}
          <Box sx={{
            minWidth: 52, height: 52, borderRadius: '13px',
            background: 'rgba(16,185,129,0.08)', border: '1.5px solid rgba(16,185,129,0.2)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#10b981', lineHeight: 1, fontFamily: "'Sora', sans-serif" }}>{day}</Typography>
            <Typography sx={{ fontSize: '0.6rem', color: '#10b981', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.8 }}>{MONTHS_SHORT[monthIdx]}</Typography>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography level="body-sm" sx={{ fontWeight: 700, fontFamily: "'Sora', sans-serif", color: '#1a1a2e' }} noWrap>
                #{item?.id} · Сделка №{deal?.id}
              </Typography>
              <Chip size="sm" sx={{ background: 'rgba(16,185,129,0.1)', color: '#10b981', fontWeight: 700, border: 'none', fontSize: '0.66rem', flexShrink: 0, ml: 1 }}>
                Свободно
              </Chip>
            </Stack>

            <Stack direction="row" alignItems="flex-start" spacing={0.7} sx={{ mb: 0.5 }}>
              <LocationOnRoundedIcon sx={{ fontSize: 13, color: '#10b981', flexShrink: 0, mt: 0.15 }} />
              <Typography level="body-xs" sx={{ color: '#1a1a2e', fontWeight: 500, lineHeight: 1.4 }}>{full}</Typography>
            </Stack>

            <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mb: 1 }}>
              {item?.time_event && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <AccessTimeRoundedIcon sx={{ fontSize: 12, color: 'text.tertiary' }} />
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>{String(item.time_event).slice(0, 5)}</Typography>
                </Stack>
              )}
              {deal?.square > 0 && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <SquareFootRoundedIcon sx={{ fontSize: 12, color: 'text.tertiary' }} />
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>{deal.square} м²</Typography>
                </Stack>
              )}
              {deal?.surveyor_salary > 0 && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <PaidRoundedIcon sx={{ fontSize: 12, color: '#10b981' }} />
                  <Typography level="body-xs" sx={{ color: '#10b981', fontWeight: 700 }}>{deal.surveyor_salary} ₽</Typography>
                </Stack>
              )}
            </Stack>

            <Button
              size="sm" fullWidth
              onClick={(e) => { e.stopPropagation(); onTake(item); }}
              sx={{
                borderRadius: '10px', fontWeight: 700, fontSize: '0.75rem',
                background: 'linear-gradient(135deg, #10b981, #34d399)',
                color: 'white',
                '&:hover': { transform: 'scale(1.01)' },
                transition: 'all 0.18s',
              }}
            >
              Откликнуться
            </Button>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}


// ─── SectionHeader ────────────────────────────────────────────────────────────
function SectionHeader({ title, count, action, actionPath }) {
  const navigate = useNavigate();
  
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Typography level="title-md" sx={{ fontWeight: 700, fontFamily: "'Sora', sans-serif", color: "text.primary", letterSpacing: "-0.2px" }}>
          {title}
        </Typography>
        {count !== undefined && (
          <Box sx={{ px: 1, py: 0.25, borderRadius: "20px", bgcolor: "#6366f112", border: "1px solid #6366f120" }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: "#6366f1" }}>{count}</Typography>
          </Box>
        )}
      </Stack>
      {/* {action && (
        <Typography
          level="body-xs"
          onClick={() => actionPath && navigate(actionPath)}
          sx={{
            color: "#6366f1", fontWeight: 600,
            cursor: actionPath ? "pointer" : "default",
            "&:hover": actionPath ? { textDecoration: "underline" } : {},
          }}
        >
          {action}
        </Typography>
      )} */}
    </Stack>
  );
}


// ─── Главная страница ─────────────────────────────────────────────────────────
export default function SurveyorPage() {
  const { account, tokenEmployee, setModalEvent, setChatModal, setCompleteModalEvent, setFailModalEvent, setSupplementModalEvent, uploadProcesses } = useContext(LayoutContext);
  const { openEventChat } = useOpenEventChat();

  const navigate = useNavigate();

  const today = new Date().toLocaleDateString("ru-RU", {
    weekday: "long", day: "numeric", month: "long",
  });

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  // В начале компонента добавь стейт:
  const [takingId, setTakingId] = useState(null);

  const completionPct = dashboard && dashboard.stats.total > 0
    ? Math.round((dashboard.stats.completed / dashboard.stats.total) * 100)
    : 0;

  const handleOpenEvent = (item) => {
    setModalEvent({ open: true, event: item });
  };

  const loadDashboard = useCallback(async (silent = false) => {
    const token = Cookies.get("auth_token") || tokenEmployee;
    if (!token) return;
    if (!silent) setLoading(true);
    try {
      const data = await APIGetSurveyorDashboard(token);
      setDashboard(data);
    } catch (e) {
      console.error("loadDashboard", e);
    } finally {
      setLoading(false);
    }
  }, [tokenEmployee]);

  useVisibleRefresh(() => loadDashboard(true), Boolean(account?.id && tokenEmployee));
  useEffect(() => {
    const refresh = () => loadDashboard(true);
    window.addEventListener('surveyor-additional-changed', refresh);
    return () => window.removeEventListener('surveyor-additional-changed', refresh);
  }, [loadDashboard]);


  const handleTakeEvent = () => navigate('/surveyor/dispatches');

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);


  // const handleChat = (event) => {
  //     const chatId = event?.event_chat?.id;
  //     if (chatId) navigate(`/chats?chatId=${chatId}`);
  // };


  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 5 }, pt: 3, pb: 6, maxWidth: 980, mx: "auto" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap');`}</style>

      {/* ── Приветствие ── */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontSize: "clamp(1.4rem, 3vw, 2rem)", fontWeight: 800, fontFamily: "'Sora', sans-serif", letterSpacing: "-0.8px", lineHeight: 1.2, color: "text.primary" }}>
          Добро пожаловать{account?.name ? `, ${account.name}` : ""}! 👋
        </Typography>
        <Typography level="body-sm" sx={{ color: "text.secondary", mt: 0.5, textTransform: "capitalize", fontWeight: 500 }}>
          {today}
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <SectionHeader title="Допобмеры" count={(dashboard?.additional || []).length} />
        {(dashboard?.additional || []).length ? <Stack spacing={1.5}>{dashboard.additional.map(item => <SurveyorEventCard key={item.id} item={item} variant="active" onRefresh={() => loadDashboard(true)} showBreakdown onChat={(item) => openEventChat(item)} />)}</Stack>
          : <Sheet variant="soft" sx={{ p: 2, borderRadius: '14px' }}><Typography level="body-sm" color="neutral">Нет назначенных допобмеров</Typography></Sheet>}
      </Box>

      {/* ── Общий прогресс месяца ── */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: "20px", border: "1.5px solid #e0e7ff", background: "linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)", boxShadow: "0 2px 16px rgba(99,102,241,0.08)", p: 0 }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Stack direction={'row'} justifyContent={"space-between"} alignItems={'center'} sx={{ width: '100%', mb: 0.75 }}>
            <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: "#6366f1", letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Прогресс за месяц
            </Typography>
            <Chip 
              variant="soft" 
              color="primary" 
              endDecorator={<StarRoundedIcon sx={{ color: '#e8d221' }} />}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography level="body-sm">Ваш рейтинг:</Typography>
                <Typography level="body-sm" color="primary" >
                  {Number.isFinite(formatRating(account?.reviews?.statistics?.average_rating)) ? Number(account.reviews.statistics.average_rating) : '—'}
                </Typography>
              </Box>
            </Chip>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={0.5}>
            <Box>
              <Typography sx={{ fontSize: "3.5rem", fontWeight: 800, fontFamily: "'Sora', sans-serif", letterSpacing: "-2px", lineHeight: 1, color: "#6366f1" }}>
                {completionPct}
                <Typography component="span" sx={{ fontSize: "1.5rem", fontWeight: 600, color: "#6366f1", opacity: 0.7 }}>%</Typography>
              </Typography>
              <Typography level="body-xs" sx={{ color: "text.secondary", mt: 0.5, fontWeight: 500 }}>
                Выполнено {dashboard?.stats?.completed ?? 0} из {dashboard?.stats?.total ?? 0} обмеров
              </Typography>
            </Box>

            <Box sx={{ width: { xs: "100%", sm: 300 } }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                <Typography level="body-xs" sx={{ color: "text.secondary", fontWeight: 500 }}>Прогресс выполнения</Typography>
                <Typography level="body-xs" sx={{ fontWeight: 800, color: "#6366f1", fontFamily: "'Sora', sans-serif" }}>
                  {dashboard?.stats?.completed ?? 0}/{dashboard?.stats?.total ?? 0}
                </Typography>
              </Stack>
              <LinearProgress value={completionPct} determinate sx={{ borderRadius: 8, height: 10, "--LinearProgress-progressColor": "#6366f1", "--LinearProgress-trackColor": "#c7d2fe" }} />
              {/* Раньше здесь было одно число earned_month — сумма БАЗ,
                  без допов и без выезда. Теперь сервер отдаёт начислено
                  и выплачено раздельно, и это реальные суммы. */}
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.25, flexWrap: "wrap", gap: 0.5 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#8b5cf6" }} />
                  <Typography level="body-xs" sx={{ color: "text.secondary" }}>
                    Начислено: <strong>
                      {Number(dashboard?.stats?.earned_month ?? 0).toLocaleString("ru-RU")} ₽
                    </strong>
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#10b981" }} />
                  <Typography level="body-xs" sx={{ color: "text.secondary" }}>
                    Выплачено: <strong>
                      {Number(dashboard?.stats?.paid_month ?? 0).toLocaleString("ru-RU")} ₽
                    </strong>
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#ef4444" }} />
                  <Typography level="body-xs" sx={{ color: "text.secondary" }}>Просрочено: <strong>{dashboard?.stats?.overdue ?? 0}</strong></Typography>
                </Stack>
              </Stack>

              {Number(dashboard?.stats?.pending_month ?? 0) > 0 && (
                <Box
                  onClick={() => navigate("/my-payments")}
                  sx={{
                    mt: 1, px: 1.25, py: 0.75, borderRadius: "10px",
                    background: "linear-gradient(135deg,#fffbeb,#fef3c7)",
                    cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                  }}
                >
                  <Typography level="body-xs" sx={{ color: "#92400e", fontWeight: 600 }}>
                    Ожидает выплаты: {Number(dashboard?.stats?.pending_month ?? 0).toLocaleString("ru-RU")} ₽
                  </Typography>
                  <Typography level="body-xs" sx={{ color: "#92400e" }}>подробнее →</Typography>
                </Box>
              )}
            </Box>

          </Stack>
        </CardContent>
      </Card>

      {/* ── Свободные события ── */}
      {(dashboard?.free_events ?? []).length > 0 && (
        <Box sx={{ mb: 4 }}>
          <SectionHeader title="Доступные заявки" count={(dashboard?.free_events ?? []).length} />
          <Stack spacing={1.5}>
              {(dashboard.free_events).map(item => (
                  <FreeEventCard
                      key={item.id}
                      item={item}
                      onOpen={handleOpenEvent}
                      onTake={handleTakeEvent}
                  />
              ))}
          </Stack>
        </Box>
      )}

      {Array.isArray(dashboard?.awaiting_amount_confirmation) && dashboard.awaiting_amount_confirmation.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography level="title-md" sx={{ mb: 1 }}>
            Подтвердите сумму{" "}
            <Chip size="sm" color="warning" variant="soft">{dashboard.awaiting_amount_confirmation.length}</Chip>
          </Typography>
          {dashboard.awaiting_amount_confirmation.map((item) => (
            <SurveyorEventCard
              key={`confirm-${item.id}`}
              item={item}
              showAmountAgreement            // ← включаем блок согласия в карточке
              onRefresh={loadDashboard}      // тот же обработчик, что и у остальных карточек
              onChat={() => openEventChat(item)}
            />
          ))}
        </Box>
      )}
      
      {/* ── Две колонки ── */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">

        {/* Предстоящие обмеры */}
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <SectionHeader title="Предстоящие обмеры" count={(dashboard?.upcoming ?? []).length} action="Все обмеры →" actionPath="/surveyor/calendar" />
          <Stack spacing={1.5}>
            {(dashboard?.upcoming ?? []).length > 0
              ? (dashboard.upcoming).map(item => <SurveyorEventCard 
                                                    key={item.id} 
                                                    item={item} 
                                                    variant="upcoming" 
                                                    onComplete={e => navigate(`/surveyor/close/${item?.bitrix_deal?.id}`)}
                                                    onFail={e => navigate(`/surveyor/fail/${item?.bitrix_deal?.id}`)}
                                                    onOpen={handleOpenEvent} 
                                                    showBreakdown={true}
                                                    onChat={() => openEventChat(item)}
                                                    onSupplement={e => navigate(surveyorSupplementPath(item))}
                                                    animate={false} 
                                                    uploadProcesses={uploadProcesses}
                                                    />)
              : (
                <Sheet variant="soft" sx={{ p: 3, borderRadius: "14px", textAlign: "center" }}>
                  <Typography level="body-sm" color="neutral">Нет предстоящих обмеров</Typography>
                </Sheet>
              )
            }
          </Stack>
        </Box>

        {/* Вертикальный разделитель */}
        <Divider orientation="vertical" sx={{ display: { xs: "none", md: "flex" } }} />

        {/* Активные / незавершённые */}
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <SectionHeader title="Необходимо закрыть обмеры" count={(dashboard?.active ?? []).length} action="Все обмеры →" actionPath="/surveyor/calendar" />
          <Stack spacing={1.5}>
            {(dashboard?.active ?? []).length > 0
              ? (dashboard.active).map(item => <SurveyorEventCard 
                                                  key={item.id} 
                                                  item={item} 
                                                  variant="active" 
                                                  onComplete={e => navigate(`/surveyor/close/${item?.bitrix_deal?.id}`)}
                                                  onFail={e => navigate(`/surveyor/fail/${item?.bitrix_deal?.id}`)}
                                                  onOpen={handleOpenEvent} 
                                                  showBreakdown={true}
                                                  onChat={() => openEventChat(item)}
                                                  onSupplement={e => navigate(surveyorSupplementPath(item))}
                                                  animate={false} 
                                                  uploadProcesses={uploadProcesses}
                                                  />)
              : (
                <Sheet variant="soft" sx={{ p: 3, borderRadius: "14px", textAlign: "center" }}>
                  <Typography level="body-sm" color="neutral">Нет активных обмеров 🎉</Typography>
                </Sheet>
              )
            }
          </Stack>
        </Box>
      </Stack>

      {/* ── Мини-статистика ── */}
      {/* <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mt: 4 }} useFlexGap> */}
        {/* <StatCard label="Всего" value={dashboard?.stats?.total ?? 0} sublabel="за месяц" icon="📋" color="#6366f1" progress={completionPct} /> */}
        {/* <StatCard label="Завершено" value={dashboard?.stats?.completed ?? 0} sublabel={`${completionPct}% от плана`} icon="✅" color="#10b981" /> */}
        {/* <StatCard label="В работе" value={dashboard?.stats?.in_progress ?? 0} sublabel="активных задач" icon="🔄" color="#f59e0b" /> */}
        {/* <StatCard label="Просрочено" value={dashboard?.stats?.overdue ?? 0} sublabel="требуют внимания" icon="⚠️" color="#ef4444" /> */}
        {/* <StatCard label="Площадь за месяц" value={`${dashboard?.stats?.total_area ?? 0}`} sublabel="м² обмерено" icon="📐" color="#0ea5e9" /> */}
        {/* <StatCard label="Заработано" value={`${dashboard?.stats?.earned_month ?? 0} ₽`} sublabel="начислено за месяц" icon="💰" color="#8b5cf6" /> */}
      {/* </Stack> */}
    </Box>
  );
}